import React from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'next-intl';
import { MemoType } from 'models/Memo';
import {
  MEMO_TYPE_ICON,
  MEMO_TYPE_OUTLINE_ICON,
} from 'elements/Memo/memoTypeIcon';
import { useMemoFormContext } from './context/FormContext';
import { MemoTypeSelectorForm } from './forms/MemoTypeSelectorForm';
import { QUICK_TYPES } from './composerTypes';
import { useSwitchMemoType } from './hooks/useSwitchMemoType';

interface Props {
  onOpen: () => void;
}

/**
 * The composer at rest: one row with the current type and a prompt on the
 * left, and quick type buttons on the right. Clicking the prompt opens the
 * full composer. Clicking a type switches to it and opens the composer.
 */
export const ComposerLauncher: React.FC<Props> = ({ onOpen }) => {
  const t = useTranslations();
  const { selectedMemoType } = useMemoFormContext();
  const switchType = useSwitchMemoType();

  // Focus moving around this row (its buttons, the type picker) must not
  // count as using the composer, or tabbing onto a button would open it.
  const keepFocusLocal = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const handleQuickTypeClick = (type: MemoType) => {
    switchType(type);
    onOpen();
  };

  return (
    <div
      data-testid="composer-launcher"
      className={styles.row}
      onFocus={keepFocusLocal}
      onBlur={keepFocusLocal}
    >
      <button
        type="button"
        onClick={onOpen}
        className={styles.open.btn}
        data-testid="composer-open"
      >
        <span className={styles.open.circle}>
          <FontAwesomeIcon
            icon={MEMO_TYPE_ICON[selectedMemoType]}
            className={styles.open.icon}
            aria-hidden="true"
          />
        </span>
        {/* Text is the default, so its name would only be noise. */}
        {selectedMemoType !== MemoType.Text && (
          <span className={styles.open.name}>
            {t(`memoTypes.${selectedMemoType}`)}
          </span>
        )}
        <span className={styles.open.prompt}>
          {t(`memoComposer.prompts.${selectedMemoType}`)}
        </span>
      </button>

      <span className={styles.divider} aria-hidden="true" />

      <div
        className={styles.quick.row}
        role="group"
        aria-label={t('memoComposer.changeType')}
      >
        {QUICK_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => handleQuickTypeClick(type)}
            title={t(`memoTypes.${type}`)}
            aria-label={t(`memoTypes.${type}`)}
            aria-pressed={type === selectedMemoType}
            data-testid={`composer-quick-type-${type}`}
            className={classNames(
              styles.quick.btn,
              type === selectedMemoType
                ? styles.quick.selected
                : styles.quick.idle,
            )}
          >
            <FontAwesomeIcon
              icon={MEMO_TYPE_OUTLINE_ICON[type]}
              aria-hidden="true"
            />
          </button>
        ))}

        <MemoTypeSelectorForm variant="more" onTypeSelected={onOpen} />
      </div>
    </div>
  );
};

const styles = {
  row: 'flex items-center gap-2 px-2.5 py-2',
  open: {
    btn: classNames(
      'flex min-w-0 flex-1 items-center gap-2.5 rounded-full py-1 pl-1.5 pr-3 text-left',
      'hover:bg-ink-secondary/10 focus:outline-hidden focus-visible:bg-ink-secondary/10',
    ),
    circle:
      'flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-fill text-white',
    icon: 'text-[13px]',
    name: 'shrink-0 text-sm font-semibold text-ink-primary',
    prompt: 'truncate text-sm text-ink-secondary',
  },
  divider: 'h-6 w-px shrink-0 bg-stroke',
  quick: {
    row: 'flex shrink-0 items-center gap-0.5',
    btn: classNames(
      'flex size-8 items-center justify-center rounded-lg border text-[15px]',
      'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-ink/40',
    ),
    idle: 'border-transparent text-ink-primary hover:bg-ink-secondary/10',
    selected:
      'border-surface-selection-border bg-surface-selection text-accent-ink',
  },
};
